import { memo, useEffect, useState } from "react";

const getStaticUrl = (path: string) => {
  const result = `${window.location.origin}/${path}`;
  window.flightLogger.log("getStaticUrl", result);
  return result;
};

const AUDIO_MIN_LEVEL = 0.0;
const AUDIO_LEVEL_TIMEOUT = 7000;

enum AudioLevelTestStatus {
  IN_PROGRESS = "in-progress",
  FAILED = "failed",
  SUCCESS = "success",
}

type StatusCallback = (status: AudioLevelTestStatus) => void;

const useAudioLevel = (stream: MediaStream | null, statusCallback: StatusCallback) => {
  const [micVolume, setMicVolume] = useState(0);

  useEffect(() => {
    if (!stream) {
      setMicVolume(0);
      return;
    }
    const audioTracks = stream.getAudioTracks();
    // enable all tracks
    audioTracks.forEach((track) => {
      const aTrack = track;
      aTrack.enabled = true;
    });

    const AudioCtx =
      typeof AudioContext !== "undefined"
        ? AudioContext
        : typeof AudioContext !== "undefined"
          ? AudioContext
          : null;
    if (!AudioCtx) return;
    const audioContext: AudioContext = new AudioCtx();
    const mediaStreamSource = audioContext.createMediaStreamSource(stream);
    let node: AudioWorkletNode;

    let status = AudioLevelTestStatus.IN_PROGRESS;

    const startProcessing = async () => {
      try {
        const startTime = new Date().getTime();
        let hasAboveThreshold = false;
        let hasAlerted = false;
        await audioContext.audioWorklet.addModule(
          getStaticUrl("av/audioWorklet/audioWorkletNode.js"),
        );
        node = new AudioWorkletNode(audioContext, "audiolevel");
        node.port.onmessage = (event) => {
          let volume = 0;
          if (event.data.slow) volume = event.data.slow;
          if (!node) return;
          setMicVolume(volume);
          if (hasAboveThreshold) return;
          if (volume > AUDIO_MIN_LEVEL && status !== AudioLevelTestStatus.SUCCESS) {
            hasAboveThreshold = true;
            status = AudioLevelTestStatus.SUCCESS;
            statusCallback(status);
            return;
          }
          if (hasAlerted) return;
          const currentTime = new Date().getTime();
          if (
            currentTime - startTime > AUDIO_LEVEL_TIMEOUT &&
            status !== AudioLevelTestStatus.FAILED
          ) {
            hasAlerted = true;
            status = AudioLevelTestStatus.FAILED;
            statusCallback(AudioLevelTestStatus.FAILED);
          }
        };

        mediaStreamSource.connect(node).connect(audioContext.destination);
      } catch (e) {
        window.flightLogger.log("AudioWorker Exception");
      }
    };
    startProcessing();
    return () => {
      node?.disconnect();
      mediaStreamSource?.disconnect();
      audioContext?.close();
    };
  }, [stream, statusCallback]);

  return micVolume;
};

type AudioLevelMeterProps = {
  stream: MediaStream | null;
  statusCallback: (status: AudioLevelTestStatus) => void;
};

const AudioLevelMeter = memo(function AudioLevelMeter({
  stream,
  statusCallback,
}: AudioLevelMeterProps) {
  const volume = useAudioLevel(stream, statusCallback);
  const color = volume > 0.05 ? "green" : "red";

  return (
    <div className="audio-level-meter">
      <div
        className="audio-level-meter_bar"
        style={{ width: `${5 + volume * 95}%`, height: "10px", backgroundColor: color }}
      />
    </div>
  );
});

export { AudioLevelMeter, useAudioLevel, AudioLevelTestStatus };
